import React, { useEffect } from "react";
import { useLang } from "../i18n/LangProvider";
import {
  Wallet, Clock3, RefreshCw, CalendarClock, Sunrise, CloudSnow,
  Sparkles, Wrench, ShieldAlert, Baby, Luggage, Dog, ShieldCheck,
  Package, Lock, Scale, Timer as HourglassAlias, Armchair as SeatAlias,
} from "lucide-react";

// Section metadata + icons (single canonical structure)
const SECTIONS = [
  { n: 1, key: "prepay", icon: Wallet },
  { n: 2, key: "hours", icon: Clock3 },
  { n: 3, key: "cancel", icon: RefreshCw },
  { n: 4, key: "modify", icon: CalendarClock },
  { n: 5, key: "offhours", icon: Sunrise },
  { n: 6, key: "wait", icon: HourglassAlias },
  { n: 7, key: "weather", icon: CloudSnow },
  { n: 8, key: "cleaning", icon: Sparkles },
  { n: 9, key: "damage", icon: Wrench },
  { n: 10, key: "safety", icon: ShieldAlert },
  { n: 11, key: "seatbelt", icon: SeatAlias },
  { n: 12, key: "children", icon: Baby },
  { n: 13, key: "luggage", icon: Luggage },
  { n: 14, key: "pets", icon: Dog },
  { n: 15, key: "fraud", icon: ShieldCheck },
  { n: 16, key: "lostfound", icon: Package },
  { n: 17, key: "privacy", icon: Lock },
  { n: 18, key: "law", icon: Scale },
];

// Content — PT canonical (matches user spec verbatim). EN + DE for main markets.
// FR/IT fall back to EN. Simple k/v map — no i18n framework overhead.
const T = {
  pt: {
    eyebrow: "Termos e Condições Gerais",
    title: "Termos e Condições",
    updated: "Última atualização",
    tocTitle: "Índice",
    intro: "Estes termos regem todos os serviços de transporte privado prestados pela Swiss AlpenLine. Ao efetuar uma reserva declara que os aceita integralmente.",
    // section titles
    prepay_t: "1. Pré-pagamento",
    hours_t: "2. Horários e sobretaxas",
    cancel_t: "3. Política de cancelamento",
    modify_t: "4. Alterações de reserva",
    offhours_t: "5. Reservas fora do horário normal",
    wait_t: "6. Tempo de espera",
    weather_t: "7. Condições meteorológicas e força maior",
    cleaning_t: "8. Limpeza extraordinária",
    damage_t: "9. Danos no veículo",
    safety_t: "10. Segurança",
    seatbelt_t: "11. Utilização do cinto de segurança",
    children_t: "12. Crianças",
    luggage_t: "13. Bagagem",
    pets_t: "14. Animais",
    fraud_t: "15. Proteção contra fraude",
    lostfound_t: "16. Objetos perdidos",
    privacy_t: "17. Proteção de dados",
    law_t: "18. Direito aplicável",
    // body
    prepay: [
      "Todas as reservas da Swiss AlpenLine são confirmadas apenas mediante pagamento integral antecipado.",
      "Os pagamentos são processados através de um prestador de serviços de pagamento seguro.",
      "Os fundos permanecem protegidos até à conclusão do serviço.",
      "Este sistema protege simultaneamente o cliente e a Swiss AlpenLine contra fraude e chargebacks indevidos.",
    ],
    hours_intro: "Todas as sobretaxas são apresentadas antes da confirmação da reserva.",
    hours_table_h: ["Faixa horária", "Horário", "Sobretaxa"],
    hours_table: [
      ["Normal", "08:00 – 19:00", "—"],
      ["Intermédio", "06:00 – 08:00 · 19:00 – 22:00", "+10 %"],
      ["Noturno", "22:00 – 06:00", "+20 %"],
    ],
    cancel_intro: "Nenhuma reserva é perdida. O valor pago será sempre reembolsado ou convertido em crédito conforme esta política.",
    cancel_table_h: ["Antecedência", "Condições"],
    cancel_table: [
      ["≥ 48 horas", "Reembolso integral"],
      ["24 – 48 horas", "50 % reembolso + 50 % crédito válido durante 6 meses"],
      ["< 24 horas ou não comparência", "Crédito de 100 % válido durante 6 meses"],
    ],
    modify: "Alterações gratuitas até 48 horas antes da recolha, sujeitas à disponibilidade. Após esse prazo aplica-se a política de cancelamento.",
    offhours_list: [
      { l: "Horário intermédio", v: "mínimo 6 h de antecedência" },
      { l: "Horário noturno", v: "mínimo 12 h de antecedência" },
    ],
    wait: [
      "45 minutos gratuitos em aeroportos.",
      "10 minutos gratuitos nas restantes recolhas.",
      "Após esse período será aplicada a tarifa de espera em vigor.",
    ],
    weather_intro: "A Swiss AlpenLine não poderá ser responsabilizada por atrasos ou impossibilidade de prestação do serviço causados por:",
    weather_list: [
      "neve", "gelo", "acidentes", "trânsito extraordinário",
      "encerramento de estradas", "greves", "decisões das autoridades",
      "aeroportos", "fenómenos naturais",
      "qualquer situação fora do controlo da empresa",
    ],
    cleaning_intro: "Caso seja necessária limpeza extraordinária do veículo devido a:",
    cleaning_list: ["vómito", "bebidas", "alimentos", "lama", "tinta", "outros resíduos"],
    cleaning_body: "Será cobrada uma taxa entre CHF 250 e CHF 500 dependendo da gravidade. Caso o veículo fique imobilizado para limpeza profissional poderão ainda ser cobrados os respetivos prejuízos.",
    damage: "Todos os danos provocados pelo cliente serão faturados pelo custo integral da reparação. A Swiss AlpenLine poderá ainda cobrar a perda de exploração durante o período de imobilização.",
    safety_intro: "É proibido:",
    safety_list: ["fumar", "utilizar cigarros eletrónicos", "consumir drogas", "transportar substâncias ilegais"],
    safety_body: "A empresa pode recusar ou interromper imediatamente o serviço caso o comportamento do passageiro coloque em risco o motorista, os passageiros ou o veículo.",
    seatbelt: "Todos os passageiros são obrigados a utilizar o cinto de segurança durante toda a viagem. A não utilização poderá constituir infração à legislação rodoviária do país onde o veículo circula. Qualquer multa ou sanção aplicada pelas autoridades será da exclusiva responsabilidade do passageiro infrator.",
    children: "As cadeiras de criança devem ser solicitadas durante a reserva. Estão sujeitas à disponibilidade.",
    luggage: "Caso a bagagem exceda a capacidade do veículo reservado, a Swiss AlpenLine poderá substituir o veículo ou aplicar custos adicionais previamente comunicados.",
    pets: "Animais de companhia são bem-vindos e devem ser comunicados previamente. Qualquer dano ou limpeza extraordinária será faturada ao cliente.",
    fraud: [
      "Reservas superiores a CHF 500 poderão exigir verificação de identidade.",
      "O nome do titular do pagamento deve corresponder ao passageiro ou responsável pela reserva.",
      "Chargebacks fraudulentos poderão ser contestados através dos registos de hora de recolha, hora de entrega, localização e comprovativos internos, podendo ainda ser comunicados às autoridades suíças.",
    ],
    lostfound: "Os objetos encontrados serão conservados durante 30 dias. O envio poderá implicar custos administrativos e de transporte.",
    privacy: "Os dados pessoais são tratados exclusivamente para execução da reserva, faturação e obrigações legais, em conformidade com a legislação suíça de proteção de dados.",
    law: "Estes termos regem-se exclusivamente pelo Direito Suíço. O foro competente é o da sede da Swiss AlpenLine, salvo disposição legal imperativa em contrário.",
    footer_tagline: "Private Transfers • Airport Transfers • Chauffeur Services",
    meta_desc: "Consulte os Termos e Condições da Swiss AlpenLine. Informações sobre reservas, pagamentos, cancelamentos, tempos de espera, proteção de dados e transporte privado na Suíça.",
  },
  en: {
    eyebrow: "General Terms & Conditions",
    title: "Terms & Conditions",
    updated: "Last updated",
    tocTitle: "Table of contents",
    intro: "These terms govern all private transport services provided by Swiss AlpenLine. By placing a booking you accept them in full.",
    prepay_t: "1. Prepayment",
    hours_t: "2. Hours & surcharges",
    cancel_t: "3. Cancellation policy",
    modify_t: "4. Booking changes",
    offhours_t: "5. Off-hours bookings",
    wait_t: "6. Waiting time",
    weather_t: "7. Weather & force majeure",
    cleaning_t: "8. Extraordinary cleaning",
    damage_t: "9. Vehicle damage",
    safety_t: "10. Safety",
    seatbelt_t: "11. Seatbelt use",
    children_t: "12. Children",
    luggage_t: "13. Luggage",
    pets_t: "14. Pets",
    fraud_t: "15. Fraud protection",
    lostfound_t: "16. Lost & found",
    privacy_t: "17. Data protection",
    law_t: "18. Governing law",
    prepay: [
      "All Swiss AlpenLine bookings are confirmed only upon full advance payment.",
      "Payments are processed through a secure payment services provider.",
      "Funds remain protected until service completion.",
      "This system protects both the client and Swiss AlpenLine against fraud and unlawful chargebacks.",
    ],
    hours_intro: "All surcharges are shown before confirmation.",
    hours_table_h: ["Tier", "Hours", "Surcharge"],
    hours_table: [
      ["Normal", "08:00 – 19:00", "—"],
      ["Intermediate", "06:00 – 08:00 · 19:00 – 22:00", "+10 %"],
      ["Night", "22:00 – 06:00", "+20 %"],
    ],
    cancel_intro: "No booking is ever lost. Every payment is either refunded or converted into credit per this policy.",
    cancel_table_h: ["Notice", "Terms"],
    cancel_table: [
      ["≥ 48 hours", "Full refund"],
      ["24 – 48 hours", "50 % refund + 50 % credit valid 6 months"],
      ["< 24 hours or no-show", "100 % credit valid 6 months"],
    ],
    modify: "Free changes up to 48 hours before pickup, subject to availability. Beyond that the cancellation policy applies.",
    offhours_list: [
      { l: "Intermediate hours", v: "min. 6 h notice" },
      { l: "Night hours", v: "min. 12 h notice" },
    ],
    wait: [
      "45 minutes free at airports.",
      "10 minutes free at other pickups.",
      "Beyond this period the standard waiting rate applies.",
    ],
    weather_intro: "Swiss AlpenLine cannot be held liable for delays or service impossibility caused by:",
    weather_list: [
      "snow", "ice", "accidents", "extraordinary traffic",
      "road closures", "strikes", "authority decisions",
      "airports", "natural phenomena",
      "any circumstance beyond the company's control",
    ],
    cleaning_intro: "If extraordinary cleaning is required due to:",
    cleaning_list: ["vomit", "spilled drinks", "food", "mud", "paint", "other residue"],
    cleaning_body: "A fee between CHF 250 and CHF 500 is charged depending on severity. If the vehicle must be taken out of service for professional cleaning, the corresponding lost income may also be charged.",
    damage: "All client-caused damage is charged at the full repair cost. Swiss AlpenLine may additionally invoice loss of use during downtime.",
    safety_intro: "The following are prohibited:",
    safety_list: ["smoking", "e-cigarettes", "drugs", "transporting illegal substances"],
    safety_body: "The company may refuse or immediately interrupt service if passenger behaviour endangers the chauffeur, passengers or the vehicle.",
    seatbelt: "All passengers must wear a seatbelt throughout the journey. Non-compliance may constitute a road-traffic offence in the country where the vehicle is operating. Any fine imposed by the authorities is the sole responsibility of the passenger concerned.",
    children: "Child seats must be requested at booking and are subject to availability.",
    luggage: "If luggage exceeds the booked vehicle's capacity, Swiss AlpenLine may substitute the vehicle or apply additional costs communicated in advance.",
    pets: "Pets are welcome and must be declared in advance. Any damage or extraordinary cleaning is invoiced to the client.",
    fraud: [
      "Bookings above CHF 500 may require identity verification.",
      "The cardholder's name must match the passenger or booking owner.",
      "Fraudulent chargebacks may be contested using pickup / drop-off timestamps, GPS location and internal records, and reported to Swiss authorities.",
    ],
    lostfound: "Lost items are stored for 30 days. Return shipping may incur administrative and courier fees.",
    privacy: "Personal data is processed exclusively to fulfil the booking, invoicing and legal obligations, in accordance with Swiss data-protection law.",
    law: "These terms are governed exclusively by Swiss law. The place of jurisdiction is the registered seat of Swiss AlpenLine, save mandatory legal provisions to the contrary.",
    footer_tagline: "Private Transfers • Airport Transfers • Chauffeur Services",
    meta_desc: "Read the Swiss AlpenLine Terms & Conditions — bookings, payments, cancellations, waiting times, data protection and private transport in Switzerland.",
  },
  de: {
    eyebrow: "Allgemeine Geschäftsbedingungen",
    title: "Allgemeine Geschäftsbedingungen",
    updated: "Zuletzt aktualisiert",
    tocTitle: "Inhaltsverzeichnis",
    intro: "Diese AGB regeln sämtliche privaten Transportleistungen von Swiss AlpenLine. Mit Ihrer Buchung erklären Sie sich vollständig einverstanden.",
    prepay_t: "1. Vorauszahlung",
    hours_t: "2. Zeitzuschläge",
    cancel_t: "3. Stornierungsbedingungen",
    modify_t: "4. Buchungsänderungen",
    offhours_t: "5. Buchungen ausserhalb der Regelzeit",
    wait_t: "6. Wartezeit",
    weather_t: "7. Wetter & höhere Gewalt",
    cleaning_t: "8. Ausserordentliche Reinigung",
    damage_t: "9. Fahrzeugschäden",
    safety_t: "10. Sicherheit",
    seatbelt_t: "11. Sicherheitsgurt",
    children_t: "12. Kinder",
    luggage_t: "13. Gepäck",
    pets_t: "14. Haustiere",
    fraud_t: "15. Betrugsschutz",
    lostfound_t: "16. Fundgegenstände",
    privacy_t: "17. Datenschutz",
    law_t: "18. Anwendbares Recht",
    prepay: [
      "Alle Reservationen von Swiss AlpenLine werden nur nach vollständiger Vorauszahlung bestätigt.",
      "Zahlungen werden über einen sicheren Zahlungsdienstleister abgewickelt.",
      "Die Beträge bleiben bis zum Abschluss der Fahrt geschützt.",
      "Dieses System schützt Kund:innen und Swiss AlpenLine gleichermassen vor Betrug und unrechtmässigen Chargebacks.",
    ],
    hours_intro: "Alle Zuschläge werden vor der Buchungsbestätigung angezeigt.",
    hours_table_h: ["Zeitzone", "Uhrzeit", "Zuschlag"],
    hours_table: [
      ["Normal", "08:00 – 19:00", "—"],
      ["Randzeit", "06:00 – 08:00 · 19:00 – 22:00", "+10 %"],
      ["Nacht", "22:00 – 06:00", "+20 %"],
    ],
    cancel_intro: "Keine Reservation geht verloren. Der bezahlte Betrag wird stets erstattet oder gemäss dieser Regelung in Guthaben umgewandelt.",
    cancel_table_h: ["Vorlaufzeit", "Bedingungen"],
    cancel_table: [
      ["≥ 48 Stunden", "Volle Rückerstattung"],
      ["24 – 48 Stunden", "50 % Rückerstattung + 50 % Guthaben, 6 Monate gültig"],
      ["< 24 Stunden oder No-Show", "100 % Guthaben, 6 Monate gültig"],
    ],
    modify: "Kostenlose Änderungen bis 48 Stunden vor Abholung, je nach Verfügbarkeit. Danach gelten die Stornobedingungen.",
    offhours_list: [
      { l: "Randzeiten", v: "min. 6 h Vorlauf" },
      { l: "Nachtstunden", v: "min. 12 h Vorlauf" },
    ],
    wait: [
      "45 Minuten kostenlos an Flughäfen.",
      "10 Minuten kostenlos an anderen Abholorten.",
      "Danach gilt der Wartetarif.",
    ],
    weather_intro: "Swiss AlpenLine haftet nicht für Verzögerungen oder Nichterbringung aufgrund von:",
    weather_list: [
      "Schnee", "Eis", "Unfällen", "aussergewöhnlichem Verkehr",
      "Strassensperrungen", "Streiks", "Behördenentscheidungen",
      "Flughäfen", "Naturereignissen",
      "sämtlichen Umständen ausserhalb der Kontrolle des Unternehmens",
    ],
    cleaning_intro: "Falls eine ausserordentliche Reinigung nötig ist wegen:",
    cleaning_list: ["Erbrochenem", "Getränken", "Speisen", "Schlamm", "Farbe", "sonstigen Rückständen"],
    cleaning_body: "Wird eine Gebühr von CHF 250 bis CHF 500 je nach Schweregrad berechnet. Wenn das Fahrzeug für professionelle Reinigung ausser Betrieb genommen werden muss, kann zusätzlich der entgangene Umsatz in Rechnung gestellt werden.",
    damage: "Alle vom Kunden verursachten Schäden werden zum vollen Reparaturpreis berechnet. Zusätzlich kann der Nutzungsausfall während der Ausfallzeit verrechnet werden.",
    safety_intro: "Verboten sind:",
    safety_list: ["Rauchen", "E-Zigaretten", "Drogen", "Transport illegaler Substanzen"],
    safety_body: "Das Unternehmen kann die Dienstleistung jederzeit verweigern oder abbrechen, wenn das Verhalten der Passagier:innen die Chauffeurin, die Fahrgäste oder das Fahrzeug gefährdet.",
    seatbelt: "Alle Fahrgäste sind verpflichtet, während der gesamten Fahrt den Sicherheitsgurt zu tragen. Ein Verstoss kann eine Ordnungswidrigkeit im jeweiligen Verkehrsraum darstellen. Bussen der Behörden gehen zu Lasten des verantwortlichen Fahrgastes.",
    children: "Kindersitze sind bei der Buchung anzufragen und je nach Verfügbarkeit erhältlich.",
    luggage: "Übersteigt das Gepäck die Kapazität des reservierten Fahrzeugs, kann Swiss AlpenLine ein anderes Fahrzeug einsetzen oder vorab kommunizierte Zusatzkosten erheben.",
    pets: "Haustiere sind willkommen und müssen vorab angemeldet werden. Schäden oder ausserordentliche Reinigung werden dem Kunden verrechnet.",
    fraud: [
      "Reservationen über CHF 500 können eine Identitätsprüfung erfordern.",
      "Der Name des Karteninhabers muss mit dem Fahrgast oder der buchenden Person übereinstimmen.",
      "Betrügerische Chargebacks werden mit Zeitstempeln, GPS-Daten und internen Belegen bestritten und ggf. an Schweizer Behörden gemeldet.",
    ],
    lostfound: "Gefundene Gegenstände werden 30 Tage aufbewahrt. Der Versand kann Verwaltungs- und Kurierkosten verursachen.",
    privacy: "Personenbezogene Daten werden ausschliesslich zur Reservationsabwicklung, Rechnungsstellung und Erfüllung gesetzlicher Pflichten verarbeitet, im Einklang mit dem Schweizer Datenschutzrecht.",
    law: "Diese AGB unterliegen ausschliesslich Schweizer Recht. Gerichtsstand ist der Sitz von Swiss AlpenLine, vorbehaltlich zwingender gesetzlicher Bestimmungen.",
    footer_tagline: "Private Transfers • Airport Transfers • Chauffeur Services",
    meta_desc: "Lesen Sie die AGB von Swiss AlpenLine — Reservationen, Zahlungen, Stornierungen, Wartezeiten, Datenschutz und privater Transport in der Schweiz.",
  },
};

const getT = (lang) => T[lang] || T.en;

const FLAG = "#B08D2F"; // gold accent from user spec

const Card = ({ n, Icon, title, children }) => (
  <section id={`sec-${n}`} className="bg-white border border-neutral-200 rounded-2xl shadow-sm p-8 md:p-10 scroll-mt-32">
    <div className="flex items-start gap-4 mb-5">
      <div
        className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center"
        style={{ background: "rgba(176, 141, 47, 0.10)", color: FLAG }}
      >
        <Icon size={20} />
      </div>
      <h2 className="text-neutral-900 text-xl md:text-2xl font-medium leading-tight tracking-tight">
        {title}
      </h2>
    </div>
    <div className="text-neutral-700 text-[15px] leading-relaxed space-y-3">{children}</div>
  </section>
);

const Table = ({ headers, rows }) => (
  <div className="overflow-x-auto -mx-4 md:mx-0 mt-2">
    <table className="w-full text-sm rounded-xl overflow-hidden border border-neutral-200">
      <thead>
        <tr className="bg-neutral-50 text-neutral-500 text-[11px] tracking-widest uppercase">
          {headers.map((h) => (
            <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="text-neutral-800">
        {rows.map((row, i) => (
          <tr key={row[0]} className={i < rows.length - 1 ? "border-b border-neutral-100" : ""}>
            {row.map((cell, j) => (
              <td key={j} className="px-4 py-3 align-top">
                {j === row.length - 1 ? (
                  <span style={{ color: cell.startsWith("+") ? FLAG : undefined }} className="font-medium">{cell}</span>
                ) : cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const List = ({ items }) => (
  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-2">
    {items.map((it) => (
      <li key={it} className="flex items-start gap-2">
        <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: FLAG }} />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const Termos = () => {
  const { lang } = useLang();
  const c = getT(lang);
  const today = new Date().toLocaleDateString(lang, { day: "2-digit", month: "long", year: "numeric" });

  // SEO tags
  useEffect(() => {
    const prevTitle = document.title;
    document.title = `Swiss AlpenLine | ${c.title}`;
    const setMeta = (name, content, attr = "name") => {
      let m = document.querySelector(`meta[${attr}="${name}"]`);
      if (!m) { m = document.createElement("meta"); m.setAttribute(attr, name); document.head.appendChild(m); }
      m.setAttribute("content", content);
      return m;
    };
    const desc = c.meta_desc || "Consulte os Termos e Condições da Swiss AlpenLine.";
    const m1 = setMeta("description", desc);
    const m2 = setMeta("og:title", `Swiss AlpenLine | ${c.title}`, "property");
    const m3 = setMeta("og:description", desc, "property");
    return () => {
      document.title = prevTitle;
      // We don't remove tags to avoid flicker; they'll be overwritten on next route.
    };
  }, [c.title]);

  return (
    <main
      className="min-h-screen"
      style={{ background: "#FAFAF7", color: "#1a1a1a" }}
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-32 pb-20">

        {/* Header */}
        <header className="mb-14 max-w-3xl">
          <p
            className="text-[11px] tracking-[0.25em] uppercase mb-4"
            style={{ color: FLAG }}
          >
            — {c.eyebrow}
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-neutral-900 leading-[1.05] tracking-tight">
            {c.title}
          </h1>
          <p className="mt-6 text-neutral-500 text-sm">
            {c.updated}: <time dateTime={new Date().toISOString().slice(0, 10)}>{today}</time>
          </p>
          <p className="mt-6 text-neutral-700 text-base leading-relaxed">{c.intro}</p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-10 lg:gap-16">

          {/* Main content */}
          <div className="space-y-6">
            <Card n={1} Icon={Wallet} title={c.prepay_t}>
              {c.prepay.map((p) => <p key={p}>{p}</p>)}
            </Card>

            <Card n={2} Icon={Clock3} title={c.hours_t}>
              <Table headers={c.hours_table_h} rows={c.hours_table} />
              <p className="text-neutral-500 text-sm mt-3">{c.hours_intro}</p>
            </Card>

            <Card n={3} Icon={RefreshCw} title={c.cancel_t}>
              <Table headers={c.cancel_table_h} rows={c.cancel_table} />
              <p className="text-neutral-500 text-sm mt-3">{c.cancel_intro}</p>
            </Card>

            <Card n={4} Icon={CalendarClock} title={c.modify_t}><p>{c.modify}</p></Card>

            <Card n={5} Icon={Sunrise} title={c.offhours_t}>
              <dl className="space-y-2">
                {c.offhours_list.map((it) => (
                  <div key={it.l} className="flex items-baseline gap-4">
                    <dt className="text-neutral-900 font-medium min-w-[180px]">{it.l}</dt>
                    <dd className="text-neutral-600">{it.v}</dd>
                  </div>
                ))}
              </dl>
            </Card>

            <Card n={6} Icon={HourglassAlias} title={c.wait_t}>
              {c.wait.map((p) => <p key={p}>{p}</p>)}
            </Card>

            <Card n={7} Icon={CloudSnow} title={c.weather_t}>
              <p>{c.weather_intro}</p>
              <List items={c.weather_list} />
            </Card>

            <Card n={8} Icon={Sparkles} title={c.cleaning_t}>
              <p>{c.cleaning_intro}</p>
              <List items={c.cleaning_list} />
              <p className="mt-4 rounded-xl p-4" style={{ background: "rgba(176, 141, 47, 0.06)", borderLeft: `3px solid ${FLAG}` }}>
                {c.cleaning_body}
              </p>
            </Card>

            <Card n={9} Icon={Wrench} title={c.damage_t}><p>{c.damage}</p></Card>

            <Card n={10} Icon={ShieldAlert} title={c.safety_t}>
              <p>{c.safety_intro}</p>
              <List items={c.safety_list} />
              <p className="mt-3">{c.safety_body}</p>
            </Card>

            <Card n={11} Icon={SeatAlias} title={c.seatbelt_t}><p>{c.seatbelt}</p></Card>
            <Card n={12} Icon={Baby} title={c.children_t}><p>{c.children}</p></Card>
            <Card n={13} Icon={Luggage} title={c.luggage_t}><p>{c.luggage}</p></Card>
            <Card n={14} Icon={Dog} title={c.pets_t}><p>{c.pets}</p></Card>

            <Card n={15} Icon={ShieldCheck} title={c.fraud_t}>
              {c.fraud.map((p) => <p key={p}>{p}</p>)}
            </Card>

            <Card n={16} Icon={Package} title={c.lostfound_t}><p>{c.lostfound}</p></Card>
            <Card n={17} Icon={Lock} title={c.privacy_t}><p>{c.privacy}</p></Card>
            <Card n={18} Icon={Scale} title={c.law_t}><p>{c.law}</p></Card>
          </div>

          {/* Sticky TOC (desktop only) */}
          <aside className="hidden lg:block">
            <nav
              aria-label={c.tocTitle}
              className="sticky top-28 bg-white border border-neutral-200 rounded-2xl p-5"
              data-testid="terms-toc"
            >
              <p className="text-[10px] tracking-[0.2em] uppercase text-neutral-400 mb-4">— {c.tocTitle}</p>
              <ol className="space-y-1.5 text-sm">
                {SECTIONS.map((s) => {
                  const title = c[`${s.key}_t`];
                  return (
                    <li key={s.n}>
                      <a
                        href={`#sec-${s.n}`}
                        className="block py-1 text-neutral-600 hover:text-neutral-900 transition-colors truncate"
                        style={{ borderLeft: `2px solid transparent`, paddingLeft: 8 }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderLeftColor = FLAG; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderLeftColor = "transparent"; }}
                      >
                        {title}
                      </a>
                    </li>
                  );
                })}
              </ol>
            </nav>
          </aside>
        </div>

        {/* Legal footer */}
        <footer className="mt-24 pt-10 border-t border-neutral-200 text-center">
          <p className="wordmark text-3xl md:text-4xl text-neutral-900">Swiss AlpenLine</p>
          <p className="mt-3 text-sm text-neutral-500 tracking-wide">{c.footer_tagline}</p>
          <a
            href="https://www.swissalpenline.ch"
            className="mt-3 inline-block text-sm hover:underline"
            style={{ color: FLAG }}
          >
            www.swissalpenline.ch
          </a>
        </footer>
      </div>
    </main>
  );
};

export default Termos;
